import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import { Box, Typography } from '@mui/material';
import { useState } from 'react';
import type { SyberryTodayWorkItem } from 'domain/people/syberryToday/types';
import { EMPTY_TASK_VALUE, getTaskAgeLabel, getTaskDateLabel } from '../../utils';
import SyberryTodayTaskStatusBadge from '../SyberryTodayTaskStatusBadge/SyberryTodayTaskStatusBadge';

enum SyberryTodayTaskDetailsTab {
  DESCRIPTION = 'description',
  NOTES = 'notes',
}

const TASK_DETAILS_TABS: Array<{
  id: SyberryTodayTaskDetailsTab;
  label: string;
}> = [
  {
    id: SyberryTodayTaskDetailsTab.DESCRIPTION,
    label: 'Description',
  },
  {
    id: SyberryTodayTaskDetailsTab.NOTES,
    label: 'Notes',
  },
];

const TASK_FACT_SX = {
  display: 'grid',
  gridTemplateColumns: '20px minmax(0, auto) minmax(0, 1fr)',
  gap: 1,
  alignItems: 'center',
  padding: 1,
  backgroundColor: 'background.default',
  color: 'textSecondary',
  fontSize: '12px',
};

type SyberryTodayTaskDetailsProps = {
  task: SyberryTodayWorkItem;
};

const SyberryTodayTaskDetails = ({ task }: SyberryTodayTaskDetailsProps) => {
  const [activeDetailsTab, setActiveDetailsTab] = useState<SyberryTodayTaskDetailsTab>(
    SyberryTodayTaskDetailsTab.DESCRIPTION,
  );

  const addedAtLabel = getTaskAgeLabel(task.addedAt);
  const addedByLabel = task.addedBy || EMPTY_TASK_VALUE;
  const assigneeLabel = task.assignee || EMPTY_TASK_VALUE;
  const dueDateLabel = getTaskDateLabel(task.dueDate);
  const taskDetailsByTab: Record<SyberryTodayTaskDetailsTab, string | undefined> = {
    [SyberryTodayTaskDetailsTab.DESCRIPTION]: task.description,
    [SyberryTodayTaskDetailsTab.NOTES]: task.notes,
  };
  const activeDetailsText = taskDetailsByTab[activeDetailsTab] || EMPTY_TASK_VALUE;

  return (
    <Box
      sx={{
        position: 'relative',
        zIndex: 2,
        marginTop: 1.75,
        borderTop: 1,
        borderTopColor: 'common.separator',
        paddingTop: 1.5,
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between" gap={1.5} marginBottom={1}>
        <Typography variant="body2" color="textSecondary">
          Added by{' '}
          <Typography
            component="strong"
            color={addedByLabel === EMPTY_TASK_VALUE ? 'textSecondary' : 'primary'}
            variant="subtitle1"
          >
            {addedByLabel}
          </Typography>
          <Typography component="span" color={addedAtLabel === EMPTY_TASK_VALUE ? 'textSecondary' : 'textPrimary'}>
            {' '}
            {addedAtLabel}
          </Typography>
        </Typography>

        <SyberryTodayTaskStatusBadge status={task.status} />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          gap: 0.5,
          marginBottom: 1,
        }}
      >
        <Box sx={TASK_FACT_SX}>
          <PersonOutlineOutlinedIcon fontSize="small" />
          <Typography variant="body2">Assignee</Typography>
          <Typography
            component="strong"
            variant="subtitle1"
            color={assigneeLabel === EMPTY_TASK_VALUE ? 'textSecondary' : 'textPrimary'}
            minWidth={0}
            sx={{ overflowWrap: 'anywhere' }}
          >
            {assigneeLabel}
          </Typography>
        </Box>

        <Box sx={TASK_FACT_SX}>
          <CalendarTodayOutlinedIcon fontSize="small" />
          <Typography variant="body2">Due date</Typography>
          <Typography
            component="strong"
            variant="subtitle1"
            color={dueDateLabel === EMPTY_TASK_VALUE ? 'textSecondary' : 'textPrimary'}
            minWidth={0}
            sx={{ overflowWrap: 'anywhere' }}
          >
            {dueDateLabel}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ backgroundColor: 'background.default' }}>
        <Box
          aria-label="Task details"
          role="tablist"
          sx={{
            display: 'flex',
            gap: 3,
            padding: '0 10px',
            borderBottom: 1,
            borderBottomColor: 'common.separator',
            color: 'text.secondary',
            fontSize: '12px',
          }}
        >
          {TASK_DETAILS_TABS.map(tab => (
            <Box
              key={tab.id}
              aria-selected={activeDetailsTab === tab.id}
              component="button"
              role="tab"
              tabIndex={activeDetailsTab === tab.id ? 0 : -1}
              type="button"
              sx={{
                position: 'relative',
                padding: '9px 0 8px',
                border: 0,
                background: 'transparent',
                color: 'inherit',
                font: 'inherit',
                cursor: 'pointer',
                ...(activeDetailsTab === tab.id && {
                  color: 'text.primary',
                  fontWeight: 700,
                  '&::after': {
                    position: 'absolute',
                    right: 0,
                    bottom: '-1px',
                    left: 0,
                    height: '2px',
                    background: 'currentColor',
                    content: '""',
                  },
                }),
              }}
              onClick={() => setActiveDetailsTab(tab.id)}
            >
              {tab.label}
            </Box>
          ))}
        </Box>

        <Typography
          variant="body1"
          color={activeDetailsText === EMPTY_TASK_VALUE ? 'textSecondary' : 'textPrimary'}
          p={1.25}
          display="flex"
          gap={1}
        >
          {activeDetailsText}
        </Typography>
      </Box>
    </Box>
  );
};

export default SyberryTodayTaskDetails;
